import { useState } from 'react'
import { authApi } from '../api/auth'
import { getErrorMessage } from '@/shared/api/client'
import type { User } from '@/shared/api/types'
import { webauthnApi } from '@/features/settings/api/webauthn'
import { client, sessionToUser } from '@/shared/api/generated'
import type { SessionResponse } from '@/shared/api/generated'

interface TOTPInputProps {
  tempToken: string
  webauthnAvailable?: boolean
  onSuccess: (token: string, user: User) => void
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4))
  const binary = atob(base64 + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export function TOTPInput({ tempToken, webauthnAvailable, onSuccess }: TOTPInputProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!code || code.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    setLoading(true)
    try {
      const response = await authApi.verify2FA(tempToken, code)
      onSuccess(response.token, response.user)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleWebAuthn = async () => {
    setError('')
    setLoading(true)
    try {
      const options = await webauthnApi.loginBegin(tempToken)
      // Convert base64url strings to ArrayBuffers for WebAuthn API
      const publicKey: PublicKeyCredentialRequestOptions = {
        ...options,
        challenge: typeof options.challenge === 'string'
          ? base64urlToBuffer(options.challenge as unknown as string)
          : options.challenge,
        allowCredentials: options.allowCredentials?.map(c => ({
          ...c,
          id: typeof c.id === 'string' ? base64urlToBuffer(c.id as unknown as string) : c.id,
        })),
      }
      const credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential
      if (!credential) {
        setError('Passkey verification cancelled')
        return
      }
      const result = await webauthnApi.loginFinish(tempToken, credential)
      // Fetch session to get user info
      const { data: session } = await client.get<SessionResponse>(
        '/session',
        { headers: { Authorization: `Bearer ${result.token}` } },
      )
      const user = sessionToUser(session)
      onSuccess(result.token, user)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  const hasWebAuthn = webauthnAvailable && typeof window !== 'undefined' && !!window.PublicKeyCredential

  return (
    <div className="login-page">
      <div className="card login-card">
        <h2>Two-Factor Authentication</h2>
        <p className="muted centered">Enter the 6-digit code from your authenticator app</p>
        
        <form onSubmit={handleSubmit}>
          {error && <div className="alert error">{error}</div>}
          
          <div className="field">
            <label htmlFor="code">2FA Code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              className="totp-input"
              value={code}
              onChange={handleChange}
              placeholder="000000"
              disabled={loading}
              autoFocus
            />
          </div>

          <button type="submit" className="btn-primary full-width" disabled={loading || code.length !== 6}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        {hasWebAuthn && (
          <>
            <div className="divider"><span>or</span></div>
            <button
              type="button"
              className="btn-secondary full-width"
              onClick={handleWebAuthn}
              disabled={loading}
              aria-label="Use passkey"
            >
              🔑 Use passkey
            </button>
          </>
        )}
      </div>
    </div>
  )
}
